import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Stack,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useNavigate } from "react-router-dom";
import config from "../../Config";

export default function PopularThisWeek() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopularProducts = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${config.apiBaseUrl}/${config.popularProducts}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch popular products");
        }

        const data = await response.json();

        const productData = Array.isArray(data?.Products) ? data.Products : [];

        const formatted = productData
          .slice(0, 5)
          .map((item) => ({
            id: item?.product?.productId,
            name: item?.product?.title || "Wardrobe Piece",
            rentAmount: Number(item?.product?.rentAmount || 0),
            image: item?.productImage?.[0] || "",
            description: item?.product?.description || "",
            city: item?.address?.city || "Location unavailable",
            availableDate:
              item?.slots?.availableDate || "Availability on request",
          }))
          .filter((item) => item.id);

        setProducts(formatted);
      } catch (error) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularProducts();
  }, []);

  const handleProductClick = (productId) => {
    navigate(`/ProductDetail/${productId}`);
  };

  return (
    <Box
      sx={{
        mt: {
          xs: 7,
          md: 11,
        },
        mb: {
          xs: 5,
          md: 8,
        },
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            sm: "flex-end",
          },
          justifyContent: "space-between",
          gap: 2,
          mb: {
            xs: 3,
            md: 4.5,
          },
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                width: 30,
                height: 1,
                background: "#977743",
              }}
            />

            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#977743",
              }}
            >
              Trending Now
            </Typography>
          </Stack>

          <Typography
            component="h2"
            sx={{
              fontFamily: "Georgia, serif",
              fontSize: {
                xs: 34,
                sm: 40,
                md: 48,
              },
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: "-0.035em",
              color: "#171717",
            }}
          >
            Popular This Week
          </Typography>
        </Box>

        {!loading && products.length > 0 && (
          <Typography
            sx={{
              fontSize: 10,
              color: "#77716a",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              pb: 0.5,
            }}
          >
            {products.length} curated pieces
          </Typography>
        )}
      </Box>

      <Divider
        sx={{
          mb: 4,
          borderColor: "#ddd7ce",
        }}
      />

      {loading ? (
        <Box
          sx={{
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress
            size={28}
            thickness={1.5}
            sx={{
              color: "#977743",
            }}
          />

          <Typography
            sx={{
              fontFamily: "Georgia, serif",
              fontSize: 16,
              color: "#77716a",
            }}
          >
            Curating this week's favourites...
          </Typography>
        </Box>
      ) : products.length === 0 ? (
        <Box
          sx={{
            minHeight: 280,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            background: "#fff",
            border: "1px solid #ddd7ce",
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "#977743",
              }}
            >
              Collection Update
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 28,
                  md: 34,
                },
                fontWeight: 400,
              }}
            >
              Nothing available here
            </Typography>

            <Typography
              sx={{
                maxWidth: 430,
                fontSize: 12,
                lineHeight: 1.8,
                color: "#77716a",
              }}
            >
              Our popular collection is being refreshed. Explore the wardrobe to
              discover something new.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Grid
          container
          spacing={{
            xs: 2,
            sm: 2.5,
            md: 3,
          }}
        >
          {products.map((product) => (
            <Grid
              item
              key={product.id}
              xs={12}
              sm={6}
              md={2.4}
              sx={{
                display: "flex",
              }}
            >
              <Card
                elevation={0}
                onClick={() => handleProductClick(product.id)}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 0,
                  overflow: "hidden",
                  background: "#fff",
                  border: "1px solid #ddd7ce",
                  cursor: "pointer",
                  transition: "transform 0.35s ease, box-shadow 0.35s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 20px 45px rgba(23,23,23,0.10)",
                  },
                  "&:hover .popular-image": {
                    transform: "scale(1.05)",
                  },
                  "&:hover .popular-arrow": {
                    background: "#171717",
                    color: "#fff",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    overflow: "hidden",
                    background: "#e9e4dc",
                  }}
                >
                  {product.image ? (
                    <CardMedia
                      component="img"
                      image={product.image}
                      alt={product.name}
                      className="popular-image"
                      sx={{
                        width: "100%",
                        height: {
                          xs: 330,
                          sm: 300,
                          md: 300,
                        },
                        objectFit: "cover",
                        transition: "transform 0.65s ease",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: {
                          xs: 330,
                          sm: 300,
                          md: 300,
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#e9e4dc",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 38,
                          color: "#968e83",
                        }}
                      >
                        HW
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      top: 14,
                      left: 14,
                      px: 1.3,
                      py: 0.7,
                      background: "rgba(247,244,239,0.94)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "#171717",
                      }}
                    >
                      Popular
                    </Typography>
                  </Box>

                  <IconButton
                    className="popular-arrow"
                    sx={{
                      position: "absolute",
                      right: 14,
                      bottom: 14,
                      width: 38,
                      height: 38,
                      borderRadius: 0,
                      background: "rgba(247,244,239,0.94)",
                      color: "#171717",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "#171717",
                      },
                    }}
                  >
                    <ArrowForwardIosRoundedIcon
                      sx={{
                        fontSize: 12,
                      }}
                    />
                  </IconButton>
                </Box>

                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: {
                      xs: 2,
                      md: 2.2,
                    },
                    "&:last-child": {
                      pb: {
                        xs: 2,
                        md: 2.2,
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: {
                        xs: 20,
                        md: 21,
                      },
                      fontWeight: 400,
                      lineHeight: 1.2,
                      textTransform: "capitalize",
                      color: "#171717",
                      mb: 1.2,
                      minHeight: 50,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={0.7}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#171717",
                      }}
                    >
                      ₹{product.rentAmount.toLocaleString("en-IN")}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "#77716a",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      / day
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                    }}
                  />

                  <Divider
                    sx={{
                      my: 1.8,
                      borderColor: "#eee9e2",
                    }}
                  />

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.7}
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{
                        fontSize: 15,
                        color: "#977743",
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#77716a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {product.city}
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      mt: 0.9,
                      fontSize: 9,
                      lineHeight: 1.5,
                      color: "#99938b",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {product.availableDate}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
